import { useAuth } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type ReactElement } from "react";
import type { AlwaysAiUser } from "../../../../shared/api-types";
import { authenticatedApi } from "@/lib/api";
import { can, landingPathForRole } from "@/lib/permissions";
import {
  activeRailRow,
  railSections,
  rosterIsTeam,
  type RailRd,
} from "@/lib/rail-sections";
import {
  findRdBySegment,
  parseStoredRecentRds,
  pushRecentRd,
  recentRdsStorageKey,
  resolveRecentRds,
  serializeRecentRds,
  showRecentRd,
  workspaceRdSegment,
} from "@/lib/recent-rds";
import { railChatHosts } from "@/lib/rd-chats";
import { unreadCountPollEnabled } from "@/lib/notifications-client";
import {
  NOTIFICATIONS_FLAG,
  PATIENT_DETAIL_FLAG,
  PATIENT_MESSAGING_FLAG,
  RD_HOME_FLAG,
  useFeatureEnabled,
} from "@/lib/use-features";
import { useUnreadNotificationCount } from "@/lib/use-notifications";
import { useRdOptions } from "@/lib/use-rd-options";
import {
  RailRdChatsEntry,
  useRailChatChoices,
  type RailChatChoices,
} from "./always-rail-chats";
import {
  AlwaysWordmark,
  RailHeadToggle,
  RailRdRow,
  RailSectionList,
  type RailLinkProps,
  type RailRowContext,
} from "./always-rail-parts";
import { RailAccountFoot } from "./rail-account";
import { RailAppointmentsEntry } from "./rail-appointments";
import { RailNotificationsRow } from "./rail-notifications-parts";
import type { ShellLookSwitch } from "./shell-header";

// The single rail's composer (NO-754): it runs the queries the sections need
// and hands real router links to the presentational parts. Each RD's chats
// nest under the first row for that RD (always-rail-chats.tsx), and the
// viewer's account row, with its menu, sits at the foot (rail-account.tsx).
// The Notifications row's unread count polls only while that row exists.
// Shell chrome, so only a shell imports it (the guard in shell-layout.test.tsx).

export interface AlwaysRailProps {
  user: AlwaysAiUser;
  pathname: string;
  collapsed: boolean;
  isDesktop: boolean;
  drawerOpen: boolean;
  onToggleCollapsed: () => void;
  lookSwitch?: ShellLookSwitch;
}

/** The Patients (NO-835) and Messages (NO-925) rows each follow their own flag. */
function useRailPracticeFlags(): {
  patientsEnabled: boolean;
  messagesEnabled: boolean;
} {
  return {
    patientsEnabled: useFeatureEnabled(PATIENT_DETAIL_FLAG),
    messagesEnabled: useFeatureEnabled(PATIENT_MESSAGING_FLAG),
  };
}

export function AlwaysRail(props: AlwaysRailProps): ReactElement {
  const { user, pathname, collapsed } = props;
  const rdHomeEnabled = useFeatureEnabled(RD_HOME_FLAG);
  const notifications = useRailNotifications(user, rdHomeEnabled, collapsed);
  const notificationsEnabled = notifications.enabled;
  const team = useRailTeam(user);
  const recent = useRecentRailRds(user, pathname);
  const model = railSections(user, {
    rdHomeEnabled,
    notificationsEnabled,
    ...useRailPracticeFlags(),
    team,
    recent,
  });
  const chatChoices = useRailChatChoices();
  const context: RailRowContext = {
    activeKey: activeRailRow(pathname, model),
    collapsed,
    renderLink: renderRailLink,
    renderRd: rdRowRenderer({
      hosts: railChatHosts(model),
      pathname,
      collapsed,
      choices: chatChoices,
    }),
    renderAppointments: (key) => (
      <RailAppointmentsEntry
        key={key}
        user={user}
        pathname={pathname}
        collapsed={collapsed}
        isDesktop={props.isDesktop}
        drawerOpen={props.drawerOpen}
      />
    ),
    renderNotifications: notifications.render,
  };

  return (
    <aside
      aria-label="Workspace navigation"
      className="flex h-full w-full flex-col overflow-y-auto overflow-x-hidden border-r border-border bg-muted px-3 pb-3.5 pt-2.5 brand:bg-background"
    >
      <RailHead
        landingPath={landingPathForRole(user, rdHomeEnabled)}
        collapsed={collapsed}
        onToggleCollapsed={props.onToggleCollapsed}
      />
      <nav aria-label="Main" className="flex flex-col gap-1 pt-1">
        <RailSectionList model={model} context={context} />
      </nav>
      <div className="min-h-3 flex-1" />
      <RailAccountFoot
        user={user}
        lookSwitch={props.lookSwitch}
        collapsed={collapsed}
        isDesktop={props.isDesktop}
      />
    </aside>
  );
}

/**
 * The Notifications row (NO-830) and its unread count. The count polls only
 * when the row is in the rail: the flag on and the My practice gate holding.
 */
function useRailNotifications(
  user: AlwaysAiUser,
  rdHomeEnabled: boolean,
  collapsed: boolean
): { enabled: boolean; render: RailRowContext["renderNotifications"] } {
  const enabled = useFeatureEnabled(NOTIFICATIONS_FLAG);
  const unread = useUnreadNotificationCount(
    unreadCountPollEnabled(user, { rdHomeEnabled, notificationsEnabled: enabled })
  );

  return {
    enabled,
    render: (key, current) => (
      <RailNotificationsRow
        key={key}
        unread={unread}
        current={current}
        collapsed={collapsed}
        renderLink={renderRailLink}
      />
    ),
  };
}

/**
 * The host row for each RD (the first row for it) carries its chats; a second
 * row for the same RD, in Recently viewed, stays a plain link.
 */
function rdRowRenderer({
  hosts,
  pathname,
  collapsed,
  choices,
}: {
  hosts: Map<string, string>;
  pathname: string;
  collapsed: boolean;
  choices: RailChatChoices;
}): NonNullable<RailRowContext["renderRd"]> {
  return function renderRd(row, current) {
    const shared = { rd: row.rd, current, collapsed, renderLink: renderRailLink };
    if (hosts.get(row.rd.id) !== row.key) return <RailRdRow {...shared} />;

    return <RailRdChatsEntry {...shared} pathname={pathname} choices={choices} />;
  };
}

function renderRailLink({
  href,
  current,
  className,
  children,
}: RailLinkProps): ReactElement {
  // `exact` keeps the router's own active marking (which sets aria-current
  // too) from also marking the RD row on its chat pages.
  const shared = {
    activeOptions: { exact: true, includeSearch: false },
    "aria-current": current ? ("page" as const) : undefined,
    className,
  };

  if (href.to === "/rds/$rdId/chats/$chatId") {
    return (
      <Link to={href.to} params={{ rdId: href.rdId, chatId: href.chatId }} {...shared}>
        {children}
      </Link>
    );
  }

  if (href.to === "/rds/$rdId" || href.to === "/rds/$rdId/chats/new") {
    return (
      <Link to={href.to} params={{ rdId: href.rdId }} {...shared}>
        {children}
      </Link>
    );
  }

  return (
    <Link to={href.to} {...shared}>
      {children}
    </Link>
  );
}

// On desktop the whole head row is the density toggle (RailHeadToggle); in the
// drawer below lg the wordmark links home.
function RailHead({
  landingPath,
  collapsed,
  onToggleCollapsed,
}: {
  landingPath: ReturnType<typeof landingPathForRole>;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}): ReactElement {
  return (
    <div className="flex h-10 items-center pb-2">
      <RailHeadToggle collapsed={collapsed} onToggle={onToggleCollapsed} />
      <Link
        to={landingPath}
        aria-label="Always AI"
        className="block text-foreground lg:hidden"
      >
        <AlwaysWordmark />
      </Link>
    </div>
  );
}

function toRailRd({ id, slug, name }: RailRd): RailRd {
  return { id, slug, name };
}

/**
 * The RDs the viewer directly supervises. A viewer whose roster is their team
 * reads it from rd-options (the server scopes it to their supervision edge). A
 * whole-roster viewer asks the roster endpoint for their own supervisor scope,
 * on the key the /rds supervisor picker uses for the same request.
 */
function useRailTeam(user: AlwaysAiUser): RailRd[] {
  const { getToken } = useAuth();
  const supervisedOnly = rosterIsTeam(user);
  const wholeRoster = can(user, "rds:read:all");
  const options = useRdOptions(supervisedOnly);
  const supervised = useQuery({
    queryKey: ["rds", user.id],
    enabled: wholeRoster,
    staleTime: 5 * 60_000,
    queryFn: () =>
      authenticatedApi<{ data: RailRd[] }>(
        getToken,
        `/api/rds?supervisor=${encodeURIComponent(user.id)}`
      ),
  });
  const source = supervisedOnly ? options.data?.data : supervised.data?.data;

  return useMemo(() => (source ?? []).map(toRailRd), [source]);
}

function readRecentIds(userId: string): string[] {
  try {
    return parseStoredRecentRds(
      window.localStorage.getItem(recentRdsStorageKey(userId))
    );
  } catch {
    return [];
  }
}

function writeRecentIds(userId: string, ids: string[]): void {
  try {
    window.localStorage.setItem(recentRdsStorageKey(userId), serializeRecentRds(ids));
  } catch {
    // Storage can be unavailable (private mode); the list just won't persist.
  }
}

/**
 * Recently viewed, for whole-roster viewers. Every RD workspace page they open
 * moves that RD to the front of the stored list; the rail shows that order on
 * the next page load, and meanwhile only adds RDs it isn't showing yet (on
 * top). Session detail does not count.
 */
function useRecentRailRds(user: AlwaysAiUser, pathname: string): RailRd[] {
  const enabled = can(user, "rds:read:all");
  const options = useRdOptions(enabled).data?.data;
  const [ids, setIds] = useState<readonly string[]>(() => readRecentIds(user.id));

  useEffect(() => {
    const segment = workspaceRdSegment(pathname);
    const rd = segment === null ? undefined : findRdBySegment(segment, options ?? []);
    if (!enabled || !rd) return;

    writeRecentIds(user.id, pushRecentRd(readRecentIds(user.id), rd.id));
    setIds((shown) => showRecentRd(shown, rd.id));
  }, [enabled, options, pathname, user.id]);

  return useMemo(() => resolveRecentRds(ids, options ?? []), [ids, options]);
}
